import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Image as ImageIcon, Pencil } from 'lucide-react';
import {
  useFilterHistories,
  useDeleteHistory,
  useSearchHistories,
  useUpdateHistoryStatus,
  useHistoryStats
} from '@/lib/api/hooks/history';
import type { History } from '@/lib/api/types';
import { showToast } from '@/lib/toast';
import { ActionDropdown, TanStackTable, Button, DeleteModal, PublishModal, UnpublishModal, Loader, type ColumnDef, type DropdownAction, Pagination } from '@/components/ui';
import HistoryStats from './HistoryStats';
import Search from '@/components/ui/Search';
import FilterModule from '@/components/ui/FilterModule';
import FilterParamsDisplay from '@/components/ui/FilterParamsDisplay';
import { useSearchFilterParams } from '@/hooks/useSearchFilterParams';
import { useHistoryFilterSections } from '@/pages/about/history/FilterModule/FILTER_SECTIONS';
import { Paragraph } from '@/components/ui/Typography';


type TabType = 'PUBLISH' | 'DRAFT';

const History: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>('PUBLISH');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [publishModalOpen, setPublishModalOpen] = useState(false);
  const [unpublishModalOpen, setUnpublishModalOpen] = useState(false);
  const [selectedHistoryForDelete, setSelectedHistoryForDelete] = useState<History | null>(null);
  const [selectedHistoryForStatusUpdate, setSelectedHistoryForStatusUpdate] = useState<History | null>(null);

  const deleteHistoryMutation = useDeleteHistory();
  const updateStatusMutation = useUpdateHistoryStatus();
  const { data: historyStats } = useHistoryStats();

  const publishedCount = historyStats?.publishedHistories?.totalCount || 0;
  const draftCount = historyStats?.draftHistories?.totalCount || 0;

  const FILTER_SECTIONS = useHistoryFilterSections();
      // ── URL-driven search / filter state ──────────────────────────────────────
      const {
        mode,
        searchTerm,
        filterState,
        page,
        size,
        setSearch,
        setFilters,
        removeFilter,
        clearFilters,
        setPage,
        apiFilters,
        getActiveFilterCount
      } = useSearchFilterParams(FILTER_SECTIONS)
    
  
  
    // Search API (when searching)
    const { data: searchData, isLoading: isSearchLoading, isError: isSearchError, refetch: refetchSearch } = useSearchHistories(
      { page, size },
    searchTerm,
      mode === 'search' 
    )
  
    // Filter API (when filtering or default)
    const { data: filterData, isLoading: isFilterLoading, isError: isFilterError, refetch: refetchFilter } = useFilterHistories(
      { page, size },
      {status: activeTab, ...apiFilters },
              mode === 'filter' || mode === 'default'
    )
  
    // Select appropriate data source: search takes priority
    const aggregateData = mode === 'search' ? searchData : filterData
    const isLoading = mode === 'search' ? isSearchLoading : isFilterLoading
    const isError = mode === 'search' ? isSearchError : isFilterError
  
    const totalPages = aggregateData?.data?.totalPage ?? 1
    const activeFilterCount = getActiveFilterCount()
    const refetchData = mode === 'search' ? refetchSearch : refetchFilter
    const emptyMessage = mode === 'search'
      ? 'No histories found matching your search criteria.'
      : mode === "filter" ?  'No histories found matching your filter criteria.'
      : 'No histories found.'

       const filteredHistories = Array.isArray(aggregateData?.data?.data) ? aggregateData.data.data : [];

  const handleAddHistory = () => {
    navigate('/admin/history/add');
  };

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const formatTime = (dateString?: string | null) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  };

  const getAuthorName = (history: History) => {
    return (history as unknown as { createdByName?: string; createdByUserName?: string }).createdByName
      || (history as unknown as { createdByName?: string; createdByUserName?: string }).createdByUserName
      || 'Unknown';
  };

  const getLastUpdated = (history: History) => {
    return (history as unknown as { updatedAt?: string | null }).updatedAt || history.datePublished;
  };

  const getInitials = (name: string) => {
    const parts = name.trim().split(' ').filter(Boolean);
    if (parts.length === 0) return '?';
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
  };

  const handleViewHistory = (history: History) => {
    navigate(`/admin/history/${history.historyId}`);
  };

  const handleEditHistory = (history: History) => {
    navigate(`/admin/history/edit/${history.historyId}`, {
      state: { history }
    });
  };

  const handleDeleteHistory = (history: History) => {
    setSelectedHistoryForDelete(history);
    setDeleteModalOpen(true);
  };

  const handlePublishHistory = (history: History) => {
    setSelectedHistoryForStatusUpdate(history);
    setPublishModalOpen(true);
  };

  const handleUnpublishHistory = (history: History) => {
    setSelectedHistoryForStatusUpdate(history);
    setUnpublishModalOpen(true);
  };

  const confirmDelete = () => {
    if (!selectedHistoryForDelete) return;

    const deleteData = {
      fileUrl: selectedHistoryForDelete.imageUrl,
      year: selectedHistoryForDelete.year,
      title: selectedHistoryForDelete.title,
      description: selectedHistoryForDelete.description
    };

    deleteHistoryMutation.mutate(
      {
        historyId: selectedHistoryForDelete.historyId,
        data: deleteData
      },
      {
        onSuccess: (response) => {
          if (response.success) {
            showToast.success('History deleted successfully');
            setDeleteModalOpen(false);
            setSelectedHistoryForDelete(null);
            refetchFilter();
          } else {
            showToast.error('Failed to delete history');
          }
        },
        onError: (error: Error) => {
          showToast.error('Failed to delete: ' + (error.message || 'Unknown error'));
        }
      }
    );
  };

  const confirmStatusUpdate = () => {
    if (!selectedHistoryForStatusUpdate) return;

    const newStatus = selectedHistoryForStatusUpdate.status === 'PUBLISH' ? 'DRAFT' : 'PUBLISH';

    updateStatusMutation.mutate(
      {
        historyId: selectedHistoryForStatusUpdate.historyId,
        data: { status: newStatus }
      },
      {
        onSuccess: () => {
          showToast.success(
            newStatus === 'PUBLISH'
              ? 'History published successfully!'
              : 'History unpublished successfully!'
          );
          setPublishModalOpen(false);
          setUnpublishModalOpen(false);
          setSelectedHistoryForStatusUpdate(null);
          refetchFilter();
        },
        onError: (error: Error) => {
          showToast.error('Failed to update status: ' + (error.message || 'Unknown error'));
        }
      }
    );
  };

  const getDropdownActions = (history: History): DropdownAction[] => {
    const actions: DropdownAction[] = [
      {
        label: 'Edit History',
        onClick: (e) => {
          e.stopPropagation();
          handleEditHistory(history);
        }
      },
      {
        label: history.status === 'PUBLISH' ? 'Unpublish' : 'Publish',
        onClick: (e) => {
          e.stopPropagation();
          if (history.status === 'PUBLISH') {
            handleUnpublishHistory(history);
          } else {
            handlePublishHistory(history);
          }
        },
        disabled: updateStatusMutation.isPending
      }
    ];

    // Only show Delete for draft histories
    if (history.status === 'DRAFT') {
      actions.push({
        label: 'Delete',
        onClick: (e) => {
          e.stopPropagation();
          handleDeleteHistory(history);
        },
        variant: 'danger' as const,
        disabled: deleteHistoryMutation.isPending
      });
    }

    return actions;
  };

  const columns: ColumnDef<History, unknown>[] = [
    {
      id: 'story',
      header: 'STORY',
      cell: ({ row }) => {
        const history = row.original;
        const lastUpdated = getLastUpdated(history);
        return (
          <div className="flex items-start gap-4 min-w-[420px]">
            <div className="w-32 h-20 rounded-lg overflow-hidden bg-gray-100 border border-gray-200 shrink-0">
              {history.imageUrl ? (
                <img src={history.imageUrl} alt={history.title} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gray-100">
                  <ImageIcon className="w-7 h-7 text-gray-400" />
                </div>
              )}
            </div>
            <div className="min-w-0">
              <h3 className="text-base font-semibold text-gray-900 break-words leading-tight">{history.title}</h3>
              <div className="mt-2 flex flex-wrap items-center gap-3 text-sm text-gray-500">
                <span className="inline-flex items-center rounded-md bg-emerald-50 px-2 py-0.5 text-emerald-700 font-semibold">{history.year}</span>
                <span>{formatDate(lastUpdated)}</span>
                <span className="text-gray-300">•</span>
                <span>ID: {history.historyId.slice(-5).padStart(5, '0')}</span>
              </div>
            </div>
          </div>
        );
      },
      meta: { headerClassName: 'px-4 py-3', cellClassName: 'px-4 py-4 align-top' },
    },
    {
      id: 'status',
      header: 'STATUS',
      cell: ({ row }) => {
        const isPublished = row.original.status === 'PUBLISH';
        return (
          <span className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-medium ${isPublished ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-blue-50 text-blue-700 border-blue-200'}`}>
            <span className={`h-2 w-2 rounded-full ${isPublished ? 'bg-emerald-500' : 'bg-blue-500'}`} />
            {isPublished ? 'Published' : 'Draft'}
          </span>
        );
      },
      meta: { headerClassName: 'px-4 py-3', cellClassName: 'px-4 py-4 whitespace-nowrap' },
    },
    {
      id: 'lastUpdated',
      header: 'LAST UPDATED',
      cell: ({ row }) => {
        const lastUpdated = getLastUpdated(row.original);
        return (
          <div>
            <p className="font-medium text-gray-900">{formatDate(lastUpdated)}</p>
            <p className="text-sm text-gray-500">{formatTime(lastUpdated)}</p>
          </div>
        );
      },
      meta: { headerClassName: 'px-4 py-3', cellClassName: 'px-4 py-4 whitespace-nowrap' },
    },
    {
      id: 'author',
      header: 'AUTHOR',
      cell: ({ row }) => {
        const author = getAuthorName(row.original);
        return (
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gray-100 text-gray-700 text-sm font-semibold flex items-center justify-center">
              {getInitials(author)}
            </div>
            <span className="font-medium text-gray-800">{author}</span>
          </div>
        );
      },
      meta: { headerClassName: 'px-4 py-3 ', cellClassName: 'px-4 py-4 whitespace-nowrap' },
    },
    {
      id: 'actions',
      header: 'ACTIONS',
      cell: ({ row }) => {
        const history = row.original;
        return (
          <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              title="Edit"
              onClick={() => handleEditHistory(history)}
              className="h-8 w-8 inline-flex items-center justify-center rounded border border-gray-200 text-gray-600 hover:bg-gray-50"
            >
              <Pencil className="w-5 h-5" />
            </button>
            <ActionDropdown id={history.historyId} actions={getDropdownActions(history)} />
          </div>
        );
      },
      meta: { headerClassName: 'px-4 py-3', cellClassName: 'px-4 py-4 whitespace-nowrap' },
    },
  ];

  const isEmptyState = !isLoading && mode === "default" && filteredHistories.length === 0;

  return (
    <div className="space-y-6">
      {/* History Stats */}
      <HistoryStats />

      {/* Tabs */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-0">
          <button
            onClick={() => {
              setActiveTab('PUBLISH');
              setPage(1);
            }}
            className={`px-6 py-2.5 text-sm font-semibold transition-colors rounded-md ${activeTab === 'PUBLISH'
                ? 'bg-wine-100 text-white'
                : 'text-gray-700 hover:bg-gray-100'
              }`}
          >
            Published ({publishedCount})
          </button>
          <button
            onClick={() => {
              setActiveTab('DRAFT');
              setPage(1);
            }}
            className={`px-6 py-2.5 text-sm font-semibold transition-colors rounded-md ${activeTab === 'DRAFT'
                ? 'bg-wine-100 text-white'
                : 'text-gray-700 hover:bg-gray-100'
              }`}
          >
            Drafts ({draftCount})
          </button>
        </div>
        <button
          onClick={handleAddHistory}
          className="bg-[#047857] hover:bg-[#059669] text-white font-semibold py-2.5 px-6 rounded-md text-sm"
        >
          Add New
        </button>
      </div>
                                         {/* Search + Filter Controls */}
          <div className="flex flex-col gap-4 p-4 border border-gray-200 rounded-md bg-white shadow-sm">
            <div className="flex items-center relative gap-4 justify-between flex-wrap">
              <Search
                value={searchTerm}
                onSearch={setSearch}
                placeholder="Search histories..."
              />
               <FilterModule
                    sections={FILTER_SECTIONS}
                    activeFilters={filterState}
                    onApply={setFilters}
                    activeFilterCount={activeFilterCount}
                  />
            </div>

            {/* Active filter chips — only shown in filter mode */}
            <FilterParamsDisplay
              sections={FILTER_SECTIONS}
              filterState={filterState}
              onRemove={removeFilter}
              onClearAll={clearFilters}
            />
          </div>

      {/* History List */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm relative">
        {isLoading && filteredHistories.length === 0 && (
          <div className="flex items-center justify-center py-20">
            <Loader size="md" variant="wine" message="Loading histories..." />
          </div>
        )}
        {isLoading && filteredHistories.length > 0 && (
          <div className="absolute top-0 left-0 right-0 h-1 bg-gray-200 z-10">
            <div className="h-full bg-[#681628] animate-pulse" style={{ width: '40%' }} />
          </div>
        )}

            {isError ? (
          <div className="p-8 flex items-center justify-center  flex-col gap-4 text-center">
            <Paragraph className="">Error loading data.</Paragraph>
            <Button
              onClick={() => {
                refetchData();
              }}
              className=""
            >
              Retry
            </Button>
          </div>
        ) : isEmptyState ? <div className="text-center py-20">
                <div className="inline-block bg-gray-100 p-4 rounded-full mb-4">
                  <img src="/No-post-icon.png" alt="No history" className="w-10 h-10 object-contain" />
                </div>
                <h3 className="font-semibold text-gray-800 text-lg">
                  {emptyMessage}
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  { activeTab === 'PUBLISH' ? 'Upload your first history to get started' :  activeTab === 'DRAFT' ? 'No draft histories yet' : 'No Data Found'}
                </p>
                  <Button variant="primary" className="mt-4" onClick={handleAddHistory}>
                    Add History
                  </Button>
              </div> : (
              <>
              <div className="overflow-x-auto">
                <TanStackTable
                  data={filteredHistories}
                  columns={columns}
                  getRowId={(row) => row.historyId}
                  onRowClick={handleViewHistory}
                  rowClassName="hover:bg-gray-50"
                    emptyMessage={emptyMessage}
              emptyClassName="px-6 py-12 text-center text-sm text-gray-500"
                />
              </div>
                     <Pagination
                            currentPage={page}
                            totalPages={totalPages}
                            onPageChange={setPage}
                            className="p-4"
                          />
              </>
            )}      
      </div>

      {/* Delete Modal */}
      <DeleteModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={confirmDelete}
        title="Delete History"
        subtitle="Are you sure you want to delete this history?"
        itemName={selectedHistoryForDelete?.title}
        warningText="This action cannot be undone. The history will be permanently deleted."
        isLoading={deleteHistoryMutation.isPending}
      />

      {/* Publish Modal */}
      {selectedHistoryForStatusUpdate && selectedHistoryForStatusUpdate.status === 'DRAFT' && (
        <PublishModal
          isOpen={publishModalOpen}
          onClose={() => setPublishModalOpen(false)}
          onConfirm={confirmStatusUpdate}
          title="Publish History?"
          subtitle="Are you sure you want to publish this history?"
          itemName={selectedHistoryForStatusUpdate.title}
          infoText="This history will be visible to the public once published."
          itemType="History"
          isLoading={updateStatusMutation.isPending}
        />
      )}

      {/* Unpublish Modal */}
      {selectedHistoryForStatusUpdate && selectedHistoryForStatusUpdate.status === 'PUBLISH' && (
        <UnpublishModal
          isOpen={unpublishModalOpen}
          onClose={() => setUnpublishModalOpen(false)}
          onConfirm={confirmStatusUpdate}
          itemType="History"
          isLoading={updateStatusMutation.isPending}
        />
      )}
    </div>
  );
};

export default History;

